<template>
    <v-tooltip ref="tooltip" v-model="visible" :open-on-focus="false" :top="isTop" :bottom="isBottom" :left="left"
        :right="right" :color="bg" :content-class="contentClass" :close-delay="closeDelay" :disabled="isDisabled"
        :nudge-top="nudgeTop" :nudge-bottom="nudgeBottom">
        <template v-slot:activator="{ on, attrs }">
            <slot name="activator" :on="withClose(on)" :attrs="attrs">
                <v-btn v-bind="$attrs" v-on="withClose(on)" @click="$emit('click', $event)" :class="buttonClass">
                    <slot />
                </v-btn>
            </slot>
        </template>
        <slot name="tooltip"><span>{{ tooltip }}</span></slot>
    </v-tooltip>
</template>

<script>
export default {
    name: "ButtonTooltip",
    props: {
        tooltip: {
            type: String,
            default: ''
        },
        right: {
            type: Boolean,
            default: false,
        },
        left: {
            type: Boolean,
            default: false,
        },
        top: {
            type: Boolean,
            default: false,
        },
        bottom: {
            type: Boolean,
            default: undefined
        },
        buttonClass: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        variant: {
            type: String,
            default: '',
        },
        tapToOpen: {
            type: Boolean,
            default: false,
        },
        interactive: {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            visible: false,
            suppressed: false,
            noHover: false,
            contentBound: false,
        }
    },
    mounted() {
        const query = window.matchMedia && window.matchMedia('(hover: none)')
        this.noHover = !!query?.matches
    },
    beforeDestroy() {
        this.unbindDocument()
        this.unbindContent()
    },
    methods: {
        withClose(on) {
            const vm = this
            return {
                ...on,
                click: (event) => {
                    if (vm.isTapMode) return vm.toggleTap(event)
                    vm.suppressed = true
                    vm.visible = false
                    if (on.click) on.click(event)
                },
                mouseleave: (event) => {
                    vm.suppressed = false
                    if (on.mouseleave) on.mouseleave(event)
                },
            }
        },
        toggleTap(event) {
            const vm = this
            event.stopPropagation()
            if (vm.visible) return vm.closeTap()
            vm.visible = true
            setTimeout(() => document.addEventListener('click', vm.onDocumentTap, true), 0)
        },
        onDocumentTap(event) {
            if (event.target?.closest?.('.bp-tooltip')) return
            this.closeTap()
        },
        closeTap() {
            this.unbindDocument()
            this.visible = false
        },
        unbindDocument() {
            document.removeEventListener('click', this.onDocumentTap, true)
        },
        bindContent() {
            const vm = this
            if (vm._isDestroyed || vm.contentBound) return
            const content = vm.$refs.tooltip?.$refs?.content
            if (!content) {
                if (vm.visible) setTimeout(vm.bindContent, 50)
                return
            }
            content.addEventListener('mouseenter', vm.onContentEnter)
            content.addEventListener('mouseleave', vm.onContentLeave)
            vm.contentBound = true
        },
        unbindContent() {
            const content = this.$refs.tooltip?.$refs?.content
            if (!content) return
            content.removeEventListener('mouseenter', this.onContentEnter)
            content.removeEventListener('mouseleave', this.onContentLeave)
            this.contentBound = false
        },
        onContentEnter() {
            const vm = this
            if (vm.noHover) return
            vm.$refs.tooltip?.clearDelay?.()
            vm.visible = true
        },
        onContentLeave() {
            const vm = this
            if (vm.noHover) return
            vm.$refs.tooltip?.clearDelay?.()
            vm.visible = false
        },
    },
    watch: {
        visible(value) {
            const vm = this
            if (value && vm.suppressed) {
                vm.visible = false
                return
            }
            if (value && vm.interactive) vm.$nextTick(vm.bindContent)
        },
    },
    computed: {
        isTop() {
            return this.top;
        },
        isBottom() {
            if (this.bottom !== undefined) return this.bottom;
            return !this.right && !this.left && !this.top;
        },
        bg(vm = this) {
            return vm.$vuetify.theme.dark ? '#0C0E12' : '#FFFFFF'
        },
        isInteractive(vm = this) {
            return vm.variant === 'rich'
        },
        isTapMode(vm = this) {
            return vm.tapToOpen && vm.noHover
        },
        isDisabled(vm = this) {
            return vm.disabled || (vm.noHover && !vm.tapToOpen)
        },
        nudgeTop(vm = this) {
            return vm.interactive && vm.isBottom ? 10 : 0
        },
        nudgeBottom(vm = this) {
            return vm.interactive && vm.isTop ? 10 : 0
        },
        closeDelay(vm = this) {
            return vm.interactive ? 250 : (vm.isInteractive ? 150 : 0)
        },
        contentClass(vm = this) {
            const classes = ['bp-tooltip', vm.$vuetify.theme.dark ? 'tip-dark' : 'tip-light']
            if (vm.variant) classes.push(`tip-${vm.variant}`)
            if (vm.interactive) classes.push('tip-interactive')
            return classes.join(' ')
        }
    }
}
</script>

<style>
.bp-tooltip {
    border: 1px solid var(--v-outline-base);
    border-radius: 10px !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
    opacity: 1 !important;
    color: var(--v-txtPrimary-base) !important;
    font-size: 12px;
    font-weight: 500;
    padding: 8px 12px !important;
}

.bp-tooltip.tip-rich {
    padding: 10px 12px !important;
    text-align: left;
    pointer-events: none;
}

.bp-tooltip.tip-interactive {
    pointer-events: auto;
}

.bp-tooltip.tip-rich.tip-dark {
    border-color: rgba(204, 255, 51, 0.35);
    box-shadow: 0 10px 28px rgba(204, 255, 51, 0.15), 0 8px 24px rgba(0, 0, 0, 0.5);
}

.bp-tooltip.tip-rich.tip-light {
    border-color: rgba(51, 153, 153, 0.35);
    box-shadow: 0 10px 28px rgba(51, 153, 153, 0.15), 0 8px 24px rgba(0, 0, 0, 0.08);
}

.bp-tooltip.tip-wellness {
    border-radius: 14px !important;
    padding: 14px 16px !important;
    text-align: left;
}
</style>
